import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { Alert, AlertDescription, AlertTitle, Button, Progress } from '@mono/ui-components';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Check, CheckCircle, FileSearch, FileText, GitMerge, ListChecks, Loader2, Sparkles, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { proposalProjectApiConnect } from '../../lib/api/proposalProjectApi.connect';
// Per-document processing phases — matches reverie's proposal_processing job stages.
// Cross-document work (shred, CLINs, LCATs, compliance, amendments) happens in the
// project_analysis job and is shown in the ANALYSIS_PHASES view instead.
const PHASES = [
    {
        id: 'convert',
        label: 'Convert',
        icon: FileText,
        description: 'Converting documents with OCR'
    },
    {
        id: 'classify',
        label: 'Classify',
        icon: FileSearch,
        description: 'Identifying document types'
    },
    {
        id: 'index',
        label: 'Index',
        icon: ListChecks,
        description: 'Extracting and indexing content'
    }
];
/**
 * True when the batch (or project) does not exist server-side.
 *
 * Connect throws ConnectError(Code.NotFound); a substring match on "404" misses
 * it entirely, because the code is an enum and the message is the server's own
 * text. Kept alongside a string check so a stray REST-shaped error still stops
 * the poll rather than retrying forever.
 */ function isBatchNotFound(err) {
    if (err instanceof ConnectError && err.code === Code.NotFound) return true;
    return err instanceof Error && err.message.includes('404');
}
function determineCurrentPhase(currentStep, percentage) {
    // Fallback phase determination from Reverie's current_step.
    // Phases: 0=Convert, 1=Classify, 2=Index
    const step = currentStep.toLowerCase();
    if (step.includes('queuing') || step.includes('submitting') || percentage === 0) return -1;
    // Convert — Baltigo OCR/document processing (reverie 10-35%)
    if (step.includes('baltigo') || step.includes('ocr') || step.includes('processing document') || step.includes('initializing')) return 0;
    // Classify — document type classification (reverie 40%)
    if (step.includes('classif')) return 1;
    // Index — block extraction + storage (reverie 50-90%)
    if (step.includes('block') || step.includes('extract') || step.includes('stor') || step.includes('index') || percentage >= 50) return 2;
    return 0;
}
function PhaseIndicator({ phase, status, isLast }) {
    const Icon = phase.icon;
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex items-center",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col items-center",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: `
            relative flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-500
            ${status === 'completed' ? 'bg-blue-600 border-blue-600 text-white' : status === 'active' ? 'bg-blue-50 border-blue-600 text-blue-600 animate-pulse' : 'bg-gray-50 border-gray-300 text-gray-400'}
          `,
                        children: status === 'completed' ? /*#__PURE__*/ _jsx(Check, {
                            className: "h-5 w-5"
                        }) : status === 'active' ? /*#__PURE__*/ _jsx(Loader2, {
                            className: "h-5 w-5 animate-spin"
                        }) : /*#__PURE__*/ _jsx(Icon, {
                            className: "h-5 w-5"
                        })
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: `
            mt-2 text-xs font-medium transition-colors duration-300
            ${status === 'completed' ? 'text-blue-600' : status === 'active' ? 'text-gray-900' : 'text-gray-500'}
          `,
                        children: phase.label
                    })
                ]
            }),
            !isLast && /*#__PURE__*/ _jsx("div", {
                className: `
            w-12 h-0.5 mx-1 -mt-6 transition-all duration-500
            ${status === 'completed' ? 'bg-blue-600' : 'bg-gray-300'}
          `
            })
        ]
    });
}
const DOCUMENT_TYPE_COLORS = {
    source: {
        bg: 'bg-purple-100',
        text: 'text-purple-800',
        border: 'border-purple-300',
        label: 'Source'
    },
    amendment: {
        bg: 'bg-orange-100',
        text: 'text-orange-800',
        border: 'border-orange-300',
        label: 'Amendment'
    },
    reference: {
        bg: 'bg-blue-100',
        text: 'text-blue-800',
        border: 'border-blue-300',
        label: 'Reference'
    },
    unknown: {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        border: 'border-gray-300',
        label: 'Unknown'
    }
};
function DocumentCard({ filename, status, documentType, progress }) {
    const typeColors = documentType ? DOCUMENT_TYPE_COLORS[documentType] || DOCUMENT_TYPE_COLORS.unknown : null;
    return /*#__PURE__*/ _jsxs("div", {
        className: `
        p-2 rounded-md text-xs transition-all duration-300
        ${status === 'completed' ? 'bg-green-50 border border-green-200' : status === 'processing' ? 'bg-blue-50 border border-blue-200' : status === 'failed' ? 'bg-red-50 border border-red-200' : 'bg-gray-50 border border-gray-200'}
      `,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2",
                children: [
                    status === 'completed' ? /*#__PURE__*/ _jsx(CheckCircle, {
                        className: "w-4 h-4 text-green-600 flex-shrink-0"
                    }) : status === 'failed' ? /*#__PURE__*/ _jsx(AlertCircle, {
                        className: "w-4 h-4 text-red-600 flex-shrink-0"
                    }) : status === 'processing' ? /*#__PURE__*/ _jsx(Loader2, {
                        className: "w-4 h-4 text-blue-600 animate-spin flex-shrink-0"
                    }) : /*#__PURE__*/ _jsx(FileText, {
                        className: "w-4 h-4 text-gray-400 flex-shrink-0"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: `
            truncate flex-1
            ${status === 'completed' ? 'text-green-800' : status === 'processing' ? 'text-blue-800' : status === 'failed' ? 'text-red-800' : 'text-gray-600'}
          `,
                        title: filename,
                        children: filename
                    }),
                    typeColors && /*#__PURE__*/ _jsx("span", {
                        className: `
              px-2 py-0.5 rounded-full text-xs font-medium border flex-shrink-0
              ${typeColors.bg} ${typeColors.text} ${typeColors.border}
            `,
                        children: typeColors.label
                    })
                ]
            }),
            status === 'processing' && progress && /*#__PURE__*/ _jsxs("div", {
                className: "mt-2 space-y-1",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center justify-between text-xs",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-gray-600 truncate mr-2",
                                children: progress.currentStep
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                className: "text-gray-700 font-medium",
                                children: [
                                    progress.percentage,
                                    "%"
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Progress, {
                        value: progress.percentage,
                        className: "h-1 bg-gray-200"
                    })
                ]
            })
        ]
    });
}
/**
 * Analysis phases mapped to Reverie's project_analysis job stages.
 * Thresholds match the ProjectStage*Percent constants in reverie/pkg/pipeline/etl.go.
 */ const ANALYSIS_PHASES = [
    {
        id: 'graph',
        label: 'Document Graph',
        threshold: 10,
        icon: FileText
    },
    {
        id: 'requirements',
        label: 'Requirements & CLINs',
        threshold: 15,
        icon: ListChecks
    },
    {
        id: 'lcats',
        label: 'Labor Categories',
        threshold: 25,
        icon: Sparkles
    },
    {
        id: 'compliance',
        label: 'Compliance Matrix',
        threshold: 40,
        icon: FileSearch
    },
    {
        id: 'qa',
        label: 'Q&A Analysis',
        threshold: 55,
        icon: AlertCircle
    },
    {
        id: 'ambiguity',
        label: 'Ambiguity Detection',
        threshold: 70,
        icon: AlertCircle
    },
    {
        id: 'amendments',
        label: 'Amendment Tracking',
        threshold: 85,
        icon: GitMerge
    }
];
function AnalysisPhaseIndicators({ currentStep, percentage }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1.5",
        children: [
            ANALYSIS_PHASES.map((phase)=>{
                const isCompleted = percentage > phase.threshold;
                const isActive = !isCompleted && percentage >= phase.threshold - 14;
                const Icon = phase.icon;
                return /*#__PURE__*/ _jsxs("div", {
                    className: `
              flex items-center gap-3 px-3 py-1.5 rounded text-sm transition-all duration-300
              ${isCompleted ? 'text-blue-700' : isActive ? 'text-gray-900 bg-blue-50' : 'text-gray-400'}
            `,
                    children: [
                        isCompleted ? /*#__PURE__*/ _jsx(Check, {
                            className: "w-4 h-4 text-blue-600 flex-shrink-0"
                        }) : isActive ? /*#__PURE__*/ _jsx(Loader2, {
                            className: "w-4 h-4 text-blue-600 animate-spin flex-shrink-0"
                        }) : /*#__PURE__*/ _jsx(Icon, {
                            className: "w-4 h-4 flex-shrink-0"
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: isActive ? 'font-medium' : '',
                            children: phase.label
                        })
                    ]
                }, phase.id);
            }),
            currentStep && /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-gray-500 mt-2 px-3",
                children: currentStep
            })
        ]
    });
}
export function BatchProcessingProgress({ projectId, batchId, onComplete, onPartialComplete, onDismiss }) {
    const [isPolling, setIsPolling] = useState(true);
    const queryClient = useQueryClient();
    const nameToastShown = useRef(false);
    const completionFired = useRef(false);
    useEffect(()=>{
        completionFired.current = false;
    }, [
        batchId
    ]);
    const { data: batchStatus, isLoading, error: queryError } = useQuery({
        queryKey: [
            'batchStatus',
            projectId,
            batchId
        ],
        queryFn: async ()=>{
            return proposalProjectApiConnect.getBatchStatus(projectId, batchId);
        },
        enabled: isPolling,
        // A hidden tab pauses, as the global host does (SAD-993, SAD-997): every
        // open tab polled every 2s. On return the interval resumes, so the bar
        // catches up (and completion fires) on its next tick.
        refetchIntervalInBackground: false,
        retry: (failureCount, error)=>{
            // Don't retry when the batch does not exist. Matching on the string
            // "404" only worked against the REST client; ConnectError carries a code
            // enum and its message need not contain the number at all, so with the
            // REST path gone this silently became "retry forever".
            if (isBatchNotFound(error)) {
                return false;
            }
            // Retry other errors up to 3 times
            return failureCount < 3;
        },
        refetchInterval: (query)=>{
            const data = query.state.data;
            const trackComplete = Boolean(data?.progress?.phaseStatus?.track ?? data?.progress?.details);
            if (data?.status === 'failed') {
                return false;
            }
            if (data?.status === 'completed' && trackComplete) {
                return false;
            }
            // Stop polling if there's an error (like 404)
            if (query.state.error) {
                return false;
            }
            return 2000;
        }
    });
    // Stop polling if we get a 404 error (batch doesn't exist)
    useEffect(()=>{
        if (isBatchNotFound(queryError)) {
            setIsPolling(false);
            onDismiss?.();
        }
    }, [
        queryError,
        onDismiss
    ]);
    // Show toast + refresh project data when backend auto-renames the project
    useEffect(()=>{
        if (batchStatus?.updatedProjectName && !nameToastShown.current) {
            nameToastShown.current = true;
            toast.success(`Project renamed to "${batchStatus.updatedProjectName}"`);
            queryClient.invalidateQueries({
                queryKey: [
                    'project',
                    projectId
                ]
            });
        }
    }, [
        batchStatus?.updatedProjectName,
        projectId,
        queryClient
    ]);
    useEffect(()=>{
        const trackComplete = Boolean(batchStatus?.progress?.phaseStatus?.track ?? batchStatus?.progress?.details);
        if (batchStatus?.status === 'completed' && trackComplete) {
            setIsPolling(false);
            if (!completionFired.current) {
                completionFired.current = true;
                const hasDocumentFailures = (batchStatus.failedDocuments ?? 0) > 0;
                if (hasDocumentFailures) {
                    onPartialComplete?.();
                } else {
                    onComplete?.();
                }
            }
        } else if (batchStatus?.status === 'failed') {
            setIsPolling(false);
        }
    }, [
        batchStatus,
        onComplete,
        onPartialComplete
    ]);
    const currentPhaseIndex = useMemo(()=>{
        if (!batchStatus) return -1;
        const phaseStatus = batchStatus.progress?.phaseStatus;
        if (phaseStatus) {
            const nextIndex = PHASES.findIndex((phase)=>!phaseStatus[phase.id]);
            return nextIndex === -1 ? PHASES.length : nextIndex;
        }
        return determineCurrentPhase(batchStatus.progress.currentStep, batchStatus.progress.percentage);
    }, [
        batchStatus
    ]);
    if (isLoading && !batchStatus) {
        return /*#__PURE__*/ _jsx("div", {
            className: "bg-white border border-gray-200 rounded-lg p-6 shadow-sm",
            children: /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-3",
                children: [
                    /*#__PURE__*/ _jsx(Loader2, {
                        className: "w-5 h-5 text-blue-600 animate-spin flex-shrink-0"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex-1",
                        children: /*#__PURE__*/ _jsx("p", {
                            className: "text-sm font-medium text-gray-900",
                            children: "Initializing document processing..."
                        })
                    })
                ]
            })
        });
    }
    if (!batchStatus) {
        return null;
    }
    const { status, progress, error, documents = [] } = batchStatus;
    const { percentage = 0, currentStep = '', documentsProcessed = 0, totalDocuments = 0, details = null } = progress ?? {};
    // Completed state
    const trackComplete = Boolean(batchStatus?.progress?.phaseStatus?.track ?? batchStatus?.progress?.details);
    if (status === 'completed' && !trackComplete) {
        const analysisStatus = batchStatus.projectAnalysisStatus;
        const analysisProgress = batchStatus.projectAnalysisProgress;
        const isAnalyzing = analysisStatus === 'pending' || analysisStatus === 'processing';
        return /*#__PURE__*/ _jsxs("div", {
            className: "bg-white border border-blue-200 rounded-lg p-6 shadow-sm",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-start justify-between mb-4",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-3",
                            children: [
                                /*#__PURE__*/ _jsx(Loader2, {
                                    className: "h-5 w-5 text-blue-600 animate-spin flex-shrink-0"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-base font-semibold text-gray-900",
                                            children: isAnalyzing ? 'Cross-Document Analysis' : 'Finalizing Results…'
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-sm text-gray-600 mt-0.5",
                                            children: isAnalyzing ? 'Analyzing across all documents' : 'Generating the extraction summary'
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-3",
                            children: [
                                analysisProgress && /*#__PURE__*/ _jsxs("div", {
                                    className: "text-2xl font-bold text-blue-600",
                                    children: [
                                        Math.round(analysisProgress.percentage),
                                        "%"
                                    ]
                                }),
                                onDismiss && /*#__PURE__*/ _jsx(Button, {
                                    variant: "ghost",
                                    size: "sm",
                                    onClick: onDismiss,
                                    className: "h-8 w-8 p-0 hover:bg-gray-100",
                                    children: /*#__PURE__*/ _jsx(X, {
                                        className: "h-4 w-4"
                                    })
                                })
                            ]
                        })
                    ]
                }),
                analysisProgress && /*#__PURE__*/ _jsx("div", {
                    className: "mb-4",
                    children: /*#__PURE__*/ _jsx(Progress, {
                        value: analysisProgress.percentage,
                        className: "h-2 bg-gray-100"
                    })
                }),
                isAnalyzing && /*#__PURE__*/ _jsx(AnalysisPhaseIndicators, {
                    currentStep: analysisProgress?.currentStep,
                    percentage: analysisProgress?.percentage ?? 0
                })
            ]
        });
    }
    if (status === 'completed') {
        const failedCount = batchStatus.failedDocuments ?? 0;
        const hasDocumentFailures = failedCount > 0;
        if (hasDocumentFailures) {
            return /*#__PURE__*/ _jsxs(Alert, {
                className: "bg-yellow-50 border-yellow-200 shadow-sm",
                children: [
                    /*#__PURE__*/ _jsx(AlertCircle, {
                        className: "h-5 w-5 text-yellow-600"
                    }),
                    /*#__PURE__*/ _jsx(AlertTitle, {
                        className: "text-yellow-900 text-base",
                        children: "Processed with errors"
                    }),
                    /*#__PURE__*/ _jsxs(AlertDescription, {
                        className: "text-yellow-800",
                        children: [
                            documentsProcessed,
                            " of ",
                            totalDocuments,
                            " document",
                            totalDocuments !== 1 ? 's' : '',
                            " processed successfully. ",
                            failedCount,
                            " document",
                            failedCount !== 1 ? 's' : '',
                            " failed — re-upload or re-process to retry."
                        ]
                    }),
                    onDismiss && /*#__PURE__*/ _jsx(Button, {
                        variant: "ghost",
                        size: "sm",
                        onClick: onDismiss,
                        className: "absolute top-3 right-3 h-8 w-8 p-0 hover:bg-yellow-100",
                        children: /*#__PURE__*/ _jsx(X, {
                            className: "h-4 w-4"
                        })
                    })
                ]
            });
        }
        return /*#__PURE__*/ _jsxs(Alert, {
            className: "bg-green-50 border-green-200 shadow-sm",
            children: [
                /*#__PURE__*/ _jsx(CheckCircle, {
                    className: "h-5 w-5 text-green-600"
                }),
                /*#__PURE__*/ _jsx(AlertTitle, {
                    className: "text-green-900 text-base",
                    children: "Processing Complete! 🎉"
                }),
                /*#__PURE__*/ _jsxs(AlertDescription, {
                    className: "text-green-800",
                    children: [
                        "Successfully processed ",
                        totalDocuments,
                        " document",
                        totalDocuments !== 1 ? 's' : '',
                        " with intelligent classification and extraction.",
                        details && /*#__PURE__*/ _jsxs("div", {
                            className: "mt-3 grid grid-cols-2 gap-2 text-xs",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "bg-white rounded p-2 border border-green-200",
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "font-semibold text-green-900",
                                            children: "CLINs"
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "text-lg font-bold text-green-700",
                                            children: details.clinsExtracted
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "bg-white rounded p-2 border border-green-200",
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "font-semibold text-green-900",
                                            children: "Labor Categories"
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "text-lg font-bold text-green-700",
                                            children: details.lcatsExtracted
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "bg-white rounded p-2 border border-green-200",
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "font-semibold text-green-900",
                                            children: "Requirements"
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "text-lg font-bold text-green-700",
                                            children: details.requirementsExtracted
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "bg-white rounded p-2 border border-green-200",
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "font-semibold text-green-900",
                                            children: "Text Blocks"
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "text-lg font-bold text-green-700",
                                            children: details.blocksExtracted.toLocaleString()
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                }),
                onDismiss && /*#__PURE__*/ _jsx(Button, {
                    variant: "ghost",
                    size: "sm",
                    onClick: onDismiss,
                    className: "absolute top-3 right-3 h-8 w-8 p-0 hover:bg-green-100",
                    children: /*#__PURE__*/ _jsx(X, {
                        className: "h-4 w-4"
                    })
                })
            ]
        });
    }
    // Failed state
    if (status === 'failed') {
        return /*#__PURE__*/ _jsxs(Alert, {
            variant: "destructive",
            className: "shadow-sm",
            children: [
                /*#__PURE__*/ _jsx(AlertCircle, {
                    className: "h-5 w-5"
                }),
                /*#__PURE__*/ _jsx(AlertTitle, {
                    className: "text-base",
                    children: "Processing Failed"
                }),
                /*#__PURE__*/ _jsxs(AlertDescription, {
                    children: [
                        error || 'An error occurred while processing documents.',
                        /*#__PURE__*/ _jsxs("div", {
                            className: "mt-2 text-xs",
                            children: [
                                "Processed ",
                                documentsProcessed,
                                " of ",
                                totalDocuments,
                                " documents before failure."
                            ]
                        })
                    ]
                }),
                onDismiss && /*#__PURE__*/ _jsx(Button, {
                    variant: "ghost",
                    size: "sm",
                    onClick: onDismiss,
                    className: "absolute top-3 right-3 h-8 w-8 p-0",
                    children: /*#__PURE__*/ _jsx(X, {
                        className: "h-4 w-4"
                    })
                })
            ]
        });
    }
    // Processing state with animated phases
    return /*#__PURE__*/ _jsxs("div", {
        className: "bg-white border border-gray-200 rounded-lg p-6 shadow-sm",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-start justify-between mb-6",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("h3", {
                                className: "text-base font-semibold text-gray-900",
                                children: "Processing Documents"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-gray-600 mt-1",
                                children: currentStep
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-3",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "text-right",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "text-2xl font-bold text-blue-600",
                                        children: [
                                            percentage,
                                            "%"
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "text-xs text-gray-500",
                                        children: [
                                            documentsProcessed,
                                            "/",
                                            totalDocuments
                                        ]
                                    })
                                ]
                            }),
                            onDismiss && /*#__PURE__*/ _jsx(Button, {
                                variant: "ghost",
                                size: "sm",
                                onClick: onDismiss,
                                className: "h-8 w-8 p-0 hover:bg-gray-100",
                                children: /*#__PURE__*/ _jsx(X, {
                                    className: "h-4 w-4"
                                })
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex items-center justify-center mb-6 px-4",
                children: PHASES.map((phase, index)=>{
                    let phaseStatus = 'pending';
                    const statusFromBackend = batchStatus?.progress?.phaseStatus?.[phase.id];
                    if (typeof statusFromBackend === 'boolean') {
                        if (statusFromBackend) {
                            phaseStatus = 'completed';
                        } else if (index === currentPhaseIndex) {
                            phaseStatus = 'active';
                        }
                    } else {
                        if (index < currentPhaseIndex) {
                            phaseStatus = 'completed';
                        } else if (index === currentPhaseIndex) {
                            phaseStatus = 'active';
                        }
                    }
                    return /*#__PURE__*/ _jsx(PhaseIndicator, {
                        phase: phase,
                        status: phaseStatus,
                        isLast: index === PHASES.length - 1
                    }, phase.id);
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "mb-4",
                children: /*#__PURE__*/ _jsx(Progress, {
                    value: percentage,
                    className: "h-2 bg-gray-100"
                })
            }),
            currentPhaseIndex >= 0 && currentPhaseIndex < PHASES.length && /*#__PURE__*/ _jsx("div", {
                className: "mb-4 p-3 bg-blue-50 rounded-md border border-blue-100",
                children: /*#__PURE__*/ _jsxs("p", {
                    className: "text-xs text-blue-800",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: "font-semibold",
                            children: "Current Phase:"
                        }),
                        ' ',
                        PHASES[currentPhaseIndex].description
                    ]
                })
            }),
            documents.length > 0 && /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsxs("p", {
                        className: "text-xs font-semibold text-gray-700 mb-2",
                        children: [
                            "Documents (",
                            documentsProcessed,
                            "/",
                            totalDocuments,
                            "):"
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "space-y-2 max-h-48 overflow-y-auto",
                        children: documents.map((doc)=>/*#__PURE__*/ _jsx(DocumentCard, {
                                filename: doc.filename,
                                status: doc.status,
                                documentType: doc.documentType,
                                progress: doc.progress
                            }, doc.attachmentId))
                    })
                ]
            })
        ]
    });
}
